import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { type Me } from '../api';
import { useChat } from '../ChatEngine';
import ChatPanel from '../components/ChatPanel';

export default function Chat({ me }: { me: Me }) {
  const chat = useChat();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const convParam = searchParams.get('conv');
  const location = useLocation();
  // Les écrans (Devis, Achats, Journal…) peuvent nous passer une conversation
  // cible + un brouillon pré-rempli via l'état du routeur.
  const navState = location.state as { conv?: number; draft?: string } | null;

  // La page plein écran et la popin montrent la même conversation : les afficher
  // toutes les deux en même temps n'aurait aucun sens. La page reprend la main.
  useEffect(() => {
    chat.closeDock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fromState = navState?.conv ?? null;
    const fromUrlRaw = convParam ? Number(convParam) : null;
    const fromUrl = fromUrlRaw && !Number.isNaN(fromUrlRaw) ? fromUrlRaw : null;
    const target = fromState ?? fromUrl;
    if (target == null) return;
    chat.openConv(target);
    if (navState?.draft) chat.setInput(navState.draft);
    // Clean the URL / router state once consumed so it doesn't keep overriding navigation.
    setSearchParams({}, { replace: true });
    window.history.replaceState({}, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openConv(id: number) {
    chat.openConv(id);
    setSidebarOpen(false);
  }

  return (
    <div className={`chat-page ${sidebarOpen ? 'sidebar-open' : ''}`}>
      {/* Barre mobile de la conversation : elle vit dans le flux, sous la barre de
          titre globale. En `position: fixed` elle recouvrait le contenu. */}
      <div className="chat-mobile-bar">
        <button
          className="mobile-menu-btn"
          onClick={() => setSidebarOpen((v) => !v)}
          aria-label={sidebarOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
        >
          {sidebarOpen ? '×' : '☰'}
        </button>
        <div className="mobile-topbar-title">{chat.activeTitle || 'Nouvelle conversation'}</div>
      </div>
      <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      <aside className="sidebar">
        <header>
          <button onClick={() => { chat.newConv(); setSidebarOpen(false); }}>+ Nouvelle</button>
        </header>
        <div className="convs">
          {chat.convs.map((c) => (
            <div
              key={c.id}
              className={`conv-item ${c.id === chat.activeId ? 'active' : ''}`}
              onClick={() => openConv(c.id)}
            >
              <span>{c.title}</span>
              <button className="del" onClick={(e) => { e.stopPropagation(); chat.delConv(c.id); }}>×</button>
            </div>
          ))}
          {chat.totalConvs > chat.convs.length && (
            <Link to="/conversations" className="see-all" onClick={() => setSidebarOpen(false)}>
              Toutes les conversations ({chat.totalConvs}) →
            </Link>
          )}
        </div>
        <footer>
          <span>{me.username}{me.is_admin ? ' ★' : ''}</span>
        </footer>
      </aside>
      <main className="main">
        <ChatPanel />
      </main>
    </div>
  );
}
